import { contentRevision } from '../utils/content-definition'
import { AxiosError } from 'axios'
import {
   apiDelete,
   apiGet,
   apiPatch,
   apiPost,
} from '@forwardflow/services/Axios/configs'
import { ResponseSuccess } from '@forwardflow/types'
import CampaignService from '@forwardflow/services/CampaignService'
import { ICampaignV2Api } from './campaign-v2.api'
import {
   CampaignV2Error,
   IAgent,
   IAssistant,
   IAvailableNumber,
   IBusinessCategory,
   ICampaignConfiguration,
   ICatalogItem,
   IConfigurationRead,
   ICriteria,
   IInventoryNumber,
   ILiveCampaign,
   INumberPricing,
   IOrgAnswering,
   IPreview,
   IReadiness,
   IVoice,
   TContentKind,
} from '../interfaces/campaign-v2.types'

type TRow = Record<string, unknown>

/**
 * The BFF answers `{success, data, meta}`; the Axios interceptor already
 * unwraps the HTTP response, so `apiGet<ResponseSuccess<T>>` resolves to
 * that envelope and `.data` is the result (handoff, section 3).
 */
type TEnvelope<T> = ResponseSuccess<T> & { meta?: TRow }

/** Turn an Axios failure into a typed error the screens can branch on. */
const fail = (error: unknown): never => {
   const e = error as AxiosError<{
      message?: string
      code?: string
      error?: { code?: string; message?: string }
   }>
   const status = e?.response?.status ?? 0
   const body = e?.response?.data
   const code = body?.code || body?.error?.code
   const message =
      body?.message ||
      body?.error?.message ||
      e?.message ||
      'Something went wrong talking to the server.'
   throw new CampaignV2Error(message, status, code)
}

const call = async <T>(fn: () => Promise<T>): Promise<T> => {
   try {
      return await fn()
   } catch (error) {
      return fail(error)
   }
}

const get = <T>(apiPath: string, params?: Record<string, unknown>) =>
   apiGet<TEnvelope<T>>({ apiPath, ...(params ? { config: { params } } : {}) })
const post = <T>(
   apiPath: string,
   data?: unknown,
   headers?: Record<string, string>
) => {
   return apiPost<TEnvelope<T>>({
      apiPath,
      data,
      ...(headers ? { config: { headers } } : {}),
   })
}
const patch = <T>(apiPath: string, data?: unknown) => {
   return apiPatch<TEnvelope<T>>({ apiPath, data })
}

const str = (v: unknown) =>
   v === undefined || v === null ? undefined : String(v)
const num = (v: unknown) => (typeof v === 'number' ? v : Number(v))
const rows = (v: unknown): TRow[] =>
   Array.isArray(v)
      ? (v as TRow[])
      : v && typeof v === 'object' && Array.isArray((v as TRow).items)
        ? ((v as TRow).items as TRow[])
        : []

/** Catalog rows differ a little by kind; read the fields the editor needs. */
const toCatalogItem = (row: TRow, kind: TContentKind): ICatalogItem => {
   const def = {
      ...row,
      ...((row.eligibility as TRow) || {}),
      ...((row.definition as TRow) || {}),
   }
   const ownership = str(row.ownership || row.scope || row.visibility)
   const shared =
      typeof row.shared === 'boolean'
         ? row.shared
         : typeof row.isShared === 'boolean'
           ? row.isShared
           : ownership
             ? /shared|global|system/i.test(ownership)
             : row.organizationId === null || row.organizationId === undefined
   const counts =
      kind === 'strategy' &&
      (Array.isArray(def.sms) ||
         row.smsCount !== undefined ||
         row.emailCount !== undefined ||
         row.voicemailCount !== undefined)
         ? {
              sms: Array.isArray(def.sms)
                 ? def.sms.length
                 : num(row.smsCount || 0) || 0,
              email: Array.isArray(def.email)
                 ? def.email.length
                 : num(row.emailCount || 0) || 0,
              voicemail: Array.isArray(def.voicemail)
                 ? def.voicemail.length
                 : num(row.voicemailCount || 0) || 0,
           }
         : undefined
   return {
      contentId: num(row.contentId ?? row.id),
      kind,
      name: str(row.name || def.name) || `Untitled ${kind}`,
      description: str(row.description || def.description),
      businessTypeId:
         row.businessTypeId !== undefined ? num(row.businessTypeId) : undefined,
      shared,
      callDirections: (def.callDirections ||
         row.callDirections) as ICatalogItem['callDirections'],
      callerTypes: (def.callerTypes ||
         row.callerTypes) as ICatalogItem['callerTypes'],
      leadTypes: (def.leadTypes || row.leadTypes) as ICatalogItem['leadTypes'],
      counts,
      revisionId: str(
         row.publishedRevisionId || row.revisionId || row.currentRevisionId
      ),
   }
}

const toRevision = contentRevision

const toAssistant = (row: TRow): IAssistant => ({
   id: (row.id ?? row.assistantId) as IAssistant['id'],
   assistantName:
      str(row.assistantName || row.assistant_name || row.name) || '',
   voiceId: str(row.voiceId || row.voice_id) || '',
   description: str(row.description),
   avatar: str(row.avatar) ?? null,
   previewUrl: str(row.previewUrl || row.preview_url) ?? null,
   gender: str(row.gender) ?? null,
})

const phoneOf = (row: TRow): string => {
   const nested = row.phoneNumber as TRow | string | undefined
   if (typeof nested === 'string') return nested
   return str(nested?.phoneNumber) || str(row.number) || ''
}

/**
 * Follow `after` cursors until the backend stops handing one back. The
 * shared library is bigger than one page (handoff, section 4).
 */
const paginate = async <T>(
   fetchPage: (
      after: string | undefined
   ) => Promise<{ data: T[]; meta?: TRow }>,
   maxPages = 20
): Promise<T[]> => {
   const all: T[] = []
   let after: string | undefined
   for (let i = 0; i < maxPages; i += 1) {
      const page = await fetchPage(after)
      all.push(...(page.data || []))
      const next = str(
         page.meta?.nextCursor || page.meta?.after || page.meta?.cursor
      )
      if (!next || next === after) break
      after = next
   }
   return all
}

export const liveCampaignV2Api: ICampaignV2Api = {
   /* ---------- 4 read ---------- */

   readiness: () =>
      call(async () => {
         const res = await get<TRow>(
            '/organization-configurations/campaign-config-v2'
         )
         const d = res.data || {}
         return {
            enabled: Boolean(d.enabled),
            ready: Boolean(d.ready),
            reasons: Array.isArray(d.reasons) ? d.reasons.map(String) : [],
         } satisfies IReadiness
      }),

   businessCategories: () =>
      call(async () => {
         const list = await paginate<TRow>(async (after) => {
            const res = await get<TRow[]>(
               '/campaign-content/business-categories',
               { limit: 100, after }
            )
            return {
               data: rows(res.data),
               meta: {
                  ...res.meta,
                  ...(Array.isArray(res.data) ? {} : (res.data as TRow)),
               },
            }
         })
         return list.map(
            (r): IBusinessCategory => ({
               id: num(r.id ?? r.businessTypeId),
               name: str(r.name || r.label || r.slug) || `Category ${r.id}`,
               slug: str(r.slug),
            })
         )
      }),

   catalog: (kind, categoryId) =>
      call(async () => {
         const list = await paginate<TRow>(async (after) => {
            const res = await get<TRow[]>('/campaign-content/catalog', {
               kind,
               categoryId,
               limit: 25,
               after,
            })
            return {
               data: rows(res.data),
               meta: {
                  ...res.meta,
                  ...(Array.isArray(res.data) ? {} : (res.data as TRow)),
               },
            }
         }, 40)
         return list.map((r) => toCatalogItem(r, kind))
      }),

   voices: () =>
      call(async () => {
         const res = await get<TRow[]>('/campaign-assistant/voices', {
            page: 1,
            limit: 100,
         })
         return rows(res.data).map(
            (r): IVoice => ({
               voiceId: str(r.voiceId || r.voice_id || r.id) || '',
               name: str(r.name) || 'Voice',
               description: str(r.description),
               previewUrl: str(r.previewUrl || r.preview_url) ?? null,
               avatar: str(r.avatar) ?? null,
               gender: str(r.gender) ?? null,
               accent: str(r.accent) ?? null,
            })
         )
      }),

   agents: () =>
      call(async () => {
         const all: IAgent[] = []
         for (let page = 1; page <= 10; page += 1) {
            const res = await get<TRow[]>('/users', {
               page,
               limit: 100,
               isWhiteListed: true,
            })
            const list = rows(res.data)
            list
               .filter((u) => u.crmUserId !== null && u.crmUserId !== undefined)
               .forEach((u) =>
                  all.push({
                     crmUserId: num(u.crmUserId),
                     name:
                        str(u.name) ||
                        `${str(u.firstName) || ''} ${str(u.lastName) || ''}`.trim() ||
                        str(u.email) ||
                        'Agent',
                     email: str(u.email),
                     phone: str(u.phone) ?? null,
                  })
               )
            const totalPages = num(res.meta?.totalPages)
            if (
               !list.length ||
               list.length < 100 ||
               (totalPages && page >= totalPages)
            )
               break
         }
         return all
      }),

   orgAnswering: () =>
      call(async () => {
         const res = await get<TRow>('/organization-configurations')
         const d = res.data || {}
         const incoming = (d.incomingCall || d.incoming_call) as
            | TRow
            | undefined
         if (!incoming) return null
         return {
            receiver:
               (str(incoming.receiver) as IOrgAnswering['receiver']) || 'ai',
            forwardNumber: str(incoming.forwardNumber) ?? null,
            forwardAgentIds: Array.isArray(incoming.forwardAgentIds)
               ? incoming.forwardAgentIds.map(num)
               : [],
         }
      }),

   estimatedLeads: async (criteria: ICriteria[]) => {
      // The existing estimate endpoint understands the FUB fields; anything
      // unsupported conditions must never broaden the displayed audience.
      const supported = criteria.filter((c) =>
         [
            'source',
            'stage',
            'tags',
            'assigned_pond_id',
            'inquired_property',
         ].includes(c.field)
      )
      if (!supported.length || supported.length !== criteria.length) return null
      try {
         const res = await CampaignService.getEstimatedLeadsForCampaign(
            supported as unknown as Parameters<
               typeof CampaignService.getEstimatedLeadsForCampaign
            >[0]
         )
         return typeof res?.count === 'number' ? res.count : null
      } catch {
         return null
      }
   },

   /* ---------- 5, 12 content ---------- */

   acquireRevision: (kind, contentId) =>
      call(async () =>
         toRevision(
            (
               await post<TRow>('/campaign-content/revisions', {
                  kind,
                  contentId,
               })
            ).data
         )
      ),

   readRevision: (revisionId, contentId, published) =>
      call(async () =>
         toRevision(
            (
               await get<TRow>(
                  `/campaign-content/revisions/${encodeURIComponent(revisionId)}`,
                  {
                     contentId,
                     ...(published ? { published: true } : {}),
                  }
               )
            ).data
         )
      ),

   createContent: (kind, definition) =>
      call(async () =>
         toRevision(
            (
               await post<TRow>('/campaign-content/library', {
                  kind,
                  definition,
               })
            ).data
         )
      ),

   customizeContent: (reference, changes) =>
      call(async () =>
         toRevision(
            (
               await post<TRow>('/campaign-content/library/customize', {
                  reference,
                  changes,
               })
            ).data
         )
      ),

   reviseContent: (reference, changes) =>
      call(async () =>
         toRevision(
            (
               await post<TRow>('/campaign-content/library/revise', {
                  reference,
                  changes,
               })
            ).data
         )
      ),

   publishContent: (reference, expectedRevisionId) =>
      call(async () =>
         toRevision(
            (
               await post<TRow>('/campaign-content/library/publish', {
                  reference,
                  expectedRevisionId,
               })
            ).data
         )
      ),

   /* ---------- 6, 11 campaign ---------- */

   createDraft: (name, configuration, idempotencyKey) =>
      call(async () => {
         const res = await post<TRow>(
            '/active-lead-sources/configuration-drafts',
            { name, configuration },
            { 'Idempotency-Key': idempotencyKey }
         )
         const d = res.data || {}
         // A draft response carries its own numeric id too; the string
         // campaignId is the one that goes in URLs (handoff, section 3).
         const campaignId = String(d.campaignId ?? d.id)
         return liveCampaignV2Api.readConfiguration(campaignId)
      }),

   readConfiguration: (campaignId) =>
      call(async () => {
         const res = await get<TRow>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}/configuration`
         )
         const d = res.data || {}
         return {
            campaignId: String(d.campaignId ?? campaignId),
            name: str(d.campaignName || d.name) || '',
            configuration: d.configuration as ICampaignConfiguration,
            revision: num(d.revision),
            status: str(d.status) || 'draft',
            publishedVersionId: str(d.publishedVersionId) ?? null,
            activeVersionId: str(d.activeVersionId) ?? null,
            hasUnpublishedChanges: Boolean(d.hasUnpublishedChanges),
         } satisfies IConfigurationRead
      }),

   saveConfiguration: (campaignId, expectedRevision, configuration, name) =>
      call(async () => {
         await patch<TRow>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}/configuration`,
            {
               expectedRevision,
               configuration,
               ...(name !== undefined ? { name } : {}),
            }
         )
         return liveCampaignV2Api.readConfiguration(campaignId)
      }),

   readCampaign: (campaignId) =>
      call(async () => {
         const res = await get<TRow>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}`
         )
         const d = res.data || {}
         const data = (d.data as TRow) || {}
         const assistants = rows(data.aiAssistants || d.aiAssistants)
         const numbers = (d.assignedPhoneNumbers ||
            data.phoneNumbers ||
            d.phoneNumbers ||
            []) as Array<TRow | string>
         const routing = (data.agentAssignmentConfig ||
            d.agentAssignmentConfig) as TRow | null | undefined
         return {
            id: String(d.id ?? campaignId),
            campaignName: str(d.campaignName || d.name) || '',
            status: str(d.status) || 'draft',
            aiAssistants: assistants.map(toAssistant),
            phoneNumbers: numbers
               .map((n) => (typeof n === 'string' ? n : phoneOf(n)))
               .filter(Boolean),
            agentAssignmentConfig: routing
               ? {
                    enabled: Boolean(routing.enabled),
                    strategy:
                       (str(routing.strategy) as 'round_robin' | 'jump_ball') ||
                       'round_robin',
                    agentIds: Array.isArray(routing.agentIds)
                       ? routing.agentIds.map(num)
                       : [],
                 }
               : null,
         } satisfies ILiveCampaign
      }),

   preview: (campaignId) =>
      call(async () => {
         const res = await get<TRow>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}/configuration/preview`
         )
         const d = res.data || {}
         const readiness = (d.readiness as TRow) || d
         return {
            ready: Boolean(readiness.ready ?? d.ready),
            reasons: Array.isArray(readiness.reasons)
               ? readiness.reasons.map(String)
               : [],
            cadence: (d.cadence as IPreview['cadence']) || [],
            selectedPhoneNumbers: Array.isArray(d.selectedPhoneNumbers)
               ? d.selectedPhoneNumbers.map(String)
               : [],
            existingPhoneNumberCount: num(d.existingPhoneNumberCount ?? 0) || 0,
            numbersToPurchase: num(d.numbersToPurchase ?? 0) || 0,
            contentManifest: d.contentManifest,
         } satisfies IPreview
      }),

   publish: (campaignId, expectedRevision, idempotencyKey) =>
      call(async () => {
         const res = await post<TRow>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}/configuration/publish`,
            { expectedRevision },
            { 'Idempotency-Key': idempotencyKey }
         )
         const d = res.data || {}
         return { versionId: String(d.versionId ?? d.id ?? '') }
      }),

   activate: (campaignId, idempotencyKey) =>
      call(
         async () =>
            (
               await post<unknown>(
                  `/active-lead-sources/${encodeURIComponent(campaignId)}/activate`,
                  {},
                  { 'Idempotency-Key': idempotencyKey }
               )
            ).data
      ),

   setStatus: (campaignId, status) =>
      call(async () => {
         await patch<unknown>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}/status`,
            { status }
         )
      }),

   /* ---------- 10 voices and numbers ---------- */

   addAssistant: (campaignId, assistant) =>
      call(async () =>
         toAssistant(
            (
               await post<TRow>(
                  `/active-lead-sources/${encodeURIComponent(campaignId)}/ai-assistant`,
                  assistant
               )
            ).data || {}
         )
      ),

   updateAssistant: (campaignId, assistantId, patchBody) =>
      call(async () =>
         toAssistant(
            (
               await patch<TRow>(
                  `/active-lead-sources/${encodeURIComponent(campaignId)}/ai-assistant/${assistantId}`,
                  patchBody
               )
            ).data || {}
         )
      ),

   removeAssistant: (campaignId, assistantId) =>
      call(async () => {
         await apiDelete({
            apiPath: `/active-lead-sources/${encodeURIComponent(campaignId)}/ai-assistant/${assistantId}`,
         })
      }),

   inventoryNumbers: () =>
      call(async () => {
         const all: IInventoryNumber[] = []
         for (let page = 1; page <= 10; page += 1) {
            const res = await get<TRow[]>('/phone-numbers/all', {
               page,
               limit: 25,
            })
            const list = rows(res.data)
            list.forEach((r) =>
               all.push({
                  phoneNumber: phoneOf(r),
                  locality: str(
                     (r.phoneNumber as TRow)?.locality || r.locality
                  ),
                  region: str((r.phoneNumber as TRow)?.region || r.region),
                  isOrganizationNumber: Boolean(
                     (r.phoneNumber as TRow)?.ffNumber
                  ),
                  isUsedInCampaigns: Boolean(
                     r.isUsedInCampaigns ?? (num(r.campaignCount) || 0) > 0
                  ),
                  campaignIds: Array.isArray(r.campaignIds)
                     ? r.campaignIds.map(String)
                     : [],
               })
            )
            if (list.length < 25) break
         }
         return all.filter((n) => n.phoneNumber)
      }),

   availableNumbers: (search) =>
      call(async () => {
         const res = await get<TRow[]>('/phone-numbers', {
            ...(search ? { search } : {}),
            limit: search ? 1000 : 50,
         })
         return rows(res.data).map(
            (r) => ({ ...r, phoneNumber: phoneOf(r) }) as IAvailableNumber
         )
      }),

   pricing: (totalNumbers) =>
      call(async () => {
         if (!totalNumbers) return null
         const res = await get<INumberPricing>('/phone-numbers/pricings', {
            totalNumbers,
         })
         return res.data || null
      }),

   reserveNumber: (campaignId, number) =>
      call(async () => {
         await post<unknown>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}/reserve-phone-number`,
            number
         )
      }),

   assignNumbers: (campaignId, phoneNumbers) =>
      call(async () => {
         await post<unknown>(
            '/phone-numbers/assign-phone-numbers-to-campaign',
            { campaignId, phoneNumbers }
         )
      }),

   removeNumbers: (campaignId, phoneNumbers) =>
      call(async () => {
         await post<unknown>(
            `/active-lead-sources/${encodeURIComponent(campaignId)}/remove-phone-numbers`,
            { phoneNumbers }
         )
      }),
}
